import { useMessagesStore, type Message, type MessageAttachment } from '~/stores/messages'
import { useUserStore } from '~/stores/user'
import { useGroupsStore } from '~/stores/groups'
import { useChannelMemberListStore } from '~/stores/channelMemberList'
import { soundManager, SoundType } from '~/lib/soundManager'
import { loadAppSettings } from '~/lib/settings/appSettings'
import * as Sentry from '@sentry/nuxt'
import { API_BASE } from '~/lib/platform/apiBase'
import {
  applyUserProfileUpdated,
  dualCaseBoolean,
  dualCaseNumber,
  dualCaseString,
  dualCaseStringArray,
  mapRawGroupUser,
  mapRawSubscription,
} from '~/lib/signalr/payload'
import { useIdleDetection } from '~/composables/useIdleDetection'
import { createSignalRConnection, type SignalRInvocation } from '~/lib/signalr/connection'
import { BOT_TYPING_STATES, type MentionedChannel, type MentionedRole, type MentionedUser } from '~/types/messages'
import { useNotificationSettingsStore } from '~/stores/notificationSettings'
import { useBoardsStore } from '~/stores/boards'

export interface KafkaMessageEvent {
  groupId: string
  chatId: string
  messageId: string
  userId: string
  type: string
  payloadJson: string
  timestamp: number
}

// Events whose snapshot is applied as is: only the group's own fields (name, avatar, …) — the
// server no longer sends the channel list in it. It was built as the author of the change sees the
// group and went to every member, leaking hidden channels' names.
const GROUP_SNAPSHOT_EVENTS = new Set([
  'GroupUpdated',
])

// The group's channel structure (or who may see what) changed: each client reloads its own view —
// the channels it can see — and the member lists, which depend on channel permissions.
const GROUP_STRUCTURE_EVENTS = new Set([
  'CategoryCreated',
  'CategoryUpdated',
  'CategoryDeleted',
  'CategoryMoved',
  'ChannelCreated',
  'ChannelUpdated',
  'ChannelDeleted',
  'ChannelMoved',
  'ChannelPermissionsChanged',
])

const ROLE_DEFINITION_EVENTS = new Set(['RoleCreated', 'RoleUpdated', 'RoleMoved', 'RoleDeleted'])
const ROLE_ASSIGNMENT_EVENTS = new Set(['RoleAssigned', 'RoleRemoved'])

// One event reaches every online member of the group at once; each client waits a random moment
// before re-reading, so a big group doesn't answer a single change with a burst of identical
// requests. Repeated events for the same group within the window collapse into one reload.
const RELOAD_DEBOUNCE_MS = 300
const RELOAD_JITTER_MS = 1500
const reloadTimers = new Map<string, ReturnType<typeof setTimeout>>()

function scheduleReload(key: string, reload: () => void) {
  clearTimeout(reloadTimers.get(key))
  reloadTimers.set(key, setTimeout(() => {
    reloadTimers.delete(key)
    reload()
  }, RELOAD_DEBOUNCE_MS + Math.random() * RELOAD_JITTER_MS))
}

function reloadGroupStructure(groupId: string) {
  useChannelMemberListStore().invalidate(groupId)
  scheduleReload(`structure:${groupId}`, () => {
    void useGroupsStore().loadGroupDetails(groupId, 'SignalR:structure', true)
  })
}

// Presence changes all the time in a large group: the member lists refresh at most this often per
// group (throttled, not debounced — a steady stream of status changes must still get through).
const PRESENCE_REFRESH_MS = 3000
const presenceTimers = new Map<string, ReturnType<typeof setTimeout>>()

function schedulePresenceRefresh(groupId: string) {
  if (presenceTimers.has(groupId)) return
  presenceTimers.set(groupId, setTimeout(() => {
    presenceTimers.delete(groupId)
    useChannelMemberListStore().invalidate(groupId)
  }, PRESENCE_REFRESH_MS))
}

// Presence: every connection to this hub is a presence session on the server, which shows the user
// as idle once all their sessions report idle. Report this device's idleness on every change, and
// on every (re)connect — a fresh server-side session starts as active.
const { isIdle } = useIdleDetection()
function reportActivity() {
  connection.send('SetActivity', isIdle.value)
}

// Module-scoped so every `useGroupsSignalR()` call site (app.vue, index.vue, ...) shares one real
// connection instead of each opening its own socket — `reconnect()` only has any effect because of this.
const connection = createSignalRConnection({
  label: '[SignalR]',
  hubUrl: `${API_BASE}/hubs/groups`,
  connectErrorAction: 'signalr_connect',
  onInvocation: (invocation: SignalRInvocation) => {
    if (invocation.target === 'chat-events') {
      handleMessageEvent(invocation.arguments?.[0] as KafkaMessageEvent)
    } else if (invocation.target === 'channel-events') {
      handleChannelEvent(invocation.arguments?.[0] as KafkaMessageEvent)
    } else if (invocation.target === 'group-events') {
      handleGroupEvent(invocation.arguments?.[0] as KafkaMessageEvent)
    }
  },
})

// `Mentions` / `MentionChannels` of a MessageCreated/MessageUpdated payload — who/what the content's
// `<@id>` / `<#id>` tokens name, resolved by the server (camelCase or PascalCase, like the rest).
// Null when the payload has neither field (a server that doesn't send them) — an edit then keeps
// whatever the message already had instead of wiping it.
// Also `MentionRoles` / `MentionEveryone` / `MentionHere` / `ReplyToAuthorId` — what the notification
// policy needs to tell whether the message mentions me (camelCase, PascalCase or snake_case).
export interface RawMentions {
  mentions: MentionedUser[]
  mention_channels: MentionedChannel[]
  mention_roles: MentionedRole[]
  mention_everyone: boolean
  mention_here: boolean
  reply_to_author_id: string
}

function mapRawMentions(messageData: Record<string, unknown>): RawMentions | null {
  const hasAny = [
    'mentions', 'Mentions', 'mentionChannels', 'MentionChannels', 'mention_channels', 'mentionRoles', 'MentionRoles', 'mention_roles',
    'mentionEveryone', 'MentionEveryone', 'mention_everyone', 'mentionHere', 'MentionHere', 'mention_here',
    'replyToAuthorId', 'ReplyToAuthorId', 'reply_to_author_id',
  ]
    .some((key) => key in messageData)
  if (!hasAny) return null
  const rawUsers = (messageData.mentions ?? messageData.Mentions ?? []) as Record<string, unknown>[]
  const rawChannels = (messageData.mentionChannels ?? messageData.MentionChannels ?? messageData.mention_channels ?? []) as Record<string, unknown>[]
  const rawRoles = (messageData.mentionRoles ?? messageData.MentionRoles ?? messageData.mention_roles ?? []) as Record<string, unknown>[]
  return {
    mention_roles: (Array.isArray(rawRoles) ? rawRoles : []).map((raw) => ({
      id: dualCaseString(raw, 'id', 'Id'),
      name: dualCaseString(raw, 'name', 'Name'),
      color: dualCaseNumber(raw, 'color', 'Color'),
    })).filter((r) => !!r.id),
    mention_everyone: Boolean(messageData.mentionEveryone ?? messageData.MentionEveryone ?? messageData.mention_everyone),
    mention_here: Boolean(messageData.mentionHere ?? messageData.MentionHere ?? messageData.mention_here),
    reply_to_author_id: String(messageData.replyToAuthorId ?? messageData.ReplyToAuthorId ?? messageData.reply_to_author_id ?? ''),
    mentions: (Array.isArray(rawUsers) ? rawUsers : []).map((raw) => ({
      id: dualCaseString(raw, 'id', 'Id'),
      username: dualCaseString(raw, 'username', 'Username'),
      display_name: dualCaseString(raw, 'displayName', 'DisplayName'),
      avatar_url: dualCaseString(raw, 'avatarUrl', 'AvatarUrl'),
      is_bot: dualCaseBoolean(raw, 'isBot', 'IsBot'),
    })).filter((u) => !!u.id),
    mention_channels: (Array.isArray(rawChannels) ? rawChannels : []).map((raw) => ({
      id: dualCaseString(raw, 'id', 'Id'),
      name: dualCaseString(raw, 'name', 'Name'),
      type: dualCaseNumber(raw, 'type', 'Type'),
      group_id: dualCaseString(raw, 'groupId', 'GroupId'),
    })).filter((c) => !!c.id),
  }
}

function handleMessageEvent(event: KafkaMessageEvent) {
  const messagesStore = useMessagesStore()
  const userStore = useUserStore()
  const groupsStore = useGroupsStore()

  console.log('[SignalR] Received event:', event)
  if (event.type === 'MessageCreated') {
    const messageData = JSON.parse(event.payloadJson)
    const authorData = (messageData.author ?? messageData.Author ?? {}) as Record<string, unknown>
    // Same camelCase/PascalCase ambiguity as the author fields above — each raw attachment
    // needs the same normalization, otherwise a missing `content_type` (e.g. only `ContentType`
    // came through) crashes every `.content_type.startsWith(...)` call downstream in the UI.
    const rawAttachments = (messageData.attachments ?? messageData.Attachments ?? []) as Record<string, unknown>[]
    const attachments: MessageAttachment[] = rawAttachments.map((raw) => ({
      id: dualCaseString(raw, 'id', 'Id'),
      uploader_id: dualCaseString(raw, 'uploaderId', 'UploaderId'),
      file_name: dualCaseString(raw, 'fileName', 'FileName'),
      content_type: dualCaseString(raw, 'contentType', 'ContentType'),
      size_bytes: dualCaseNumber(raw, 'sizeBytes', 'SizeBytes'),
      status: dualCaseString(raw, 'status', 'Status'),
      download_url: dualCaseString(raw, 'downloadUrl', 'DownloadUrl'),
      expires_at: dualCaseString(raw, 'expiresAt', 'ExpiresAt') || undefined,
    }))
    const msg: Message = {
      id: event.messageId,
      channel_id: event.chatId,
      author: {
        id: dualCaseString(authorData, 'id', 'Id') || event.userId,
        username: dualCaseString(authorData, 'username', 'Username'),
        email: dualCaseString(authorData, 'email', 'Email'),
        display_name: dualCaseString(authorData, 'displayName', 'DisplayName'),
        description: dualCaseString(authorData, 'description', 'Description'),
        status: dualCaseString(authorData, 'status', 'Status'),
        preferred_language: dualCaseString(authorData, 'preferredLanguage', 'PreferredLanguage'),
        is_confirmed: dualCaseBoolean(authorData, 'isConfirmed', 'IsConfirmed'),
        is_bot: dualCaseBoolean(authorData, 'isBot', 'IsBot'),
        subscription: mapRawSubscription(authorData['subscription'] ?? authorData['Subscription']),
        avatar_id: dualCaseString(authorData, 'avatarId', 'AvatarId'),
        banner_id: dualCaseString(authorData, 'bannerId', 'BannerId'),
        avatar_url: dualCaseString(authorData, 'avatarUrl', 'AvatarUrl'),
      },
      content: messageData.content || messageData.Content || '',
      created_at: new Date(event.timestamp).toISOString(),
      is_edited: false,
      is_deleted: false,
      reply_to: messageData.reply_to || messageData.ReplyTo || '',
      interaction_id: messageData.interaction_id ?? messageData.InteractionId ?? null,
      interaction_command_name: messageData.interaction_command_name ?? messageData.InteractionCommandName ?? null,
      interaction_user_id: messageData.interaction_user_id ?? messageData.InteractionUserId ?? null,
      components_json: messageData.components_json ?? messageData.ComponentsJson ?? null,
      attachments,
      reactions: {},
      ...(mapRawMentions(messageData) ?? {}),
    }
    // An older server doesn't say whose message this replies to — the loaded original does.
    if (!msg.reply_to_author_id && msg.reply_to) {
      msg.reply_to_author_id = messagesStore.findMessageAnyChannel(msg.reply_to)?.message.author?.id ?? ''
    }
    messagesStore.addRealtimeMessage(msg)

    // Safety net alongside the `IsActive:false` BotTyping event: if a bot's message actually
    // lands, it's done working regardless of whether that event arrived.
    if (msg.author.is_bot) {
      messagesStore.setBotTyping(event.chatId, msg.author.id, msg.author.username, 'Thinking', false)
    }

    // Update unread status and play sound. A muted channel (or its category/group) gets neither;
    // the sound plays only when the notification policy would have sent a push (level, mentions).
    if (event.userId !== userStore.user?.id) {
      const notificationSettings = useNotificationSettingsStore()
      void notificationSettings.ensureLoaded(event.groupId)
      const muted = notificationSettings.resolveChannel(event.groupId, event.chatId).muted
      if (!muted && loadAppSettings().notifications.newMessages) {
        groupsStore.markChannelUnread(event.groupId, event.chatId)
      }
      // soundManager itself checks the sounds.messageSound setting.
      if (notificationSettings.shouldNotifyMessage(event.groupId, msg)) soundManager.play(SoundType.MESSAGE)
    }
  } else if (event.type === 'MessageUpdated') {
    const messageData = JSON.parse(event.payloadJson)
    messagesStore.updateRealtimeMessage(
      event.messageId,
      messageData.content || messageData.Content,
      messageData.components_json ?? messageData.ComponentsJson ?? null,
      mapRawMentions(messageData) ?? undefined,
    )
  } else if (event.type === 'MessageDeleted') {
    messagesStore.deleteRealtimeMessage(event.messageId)
  } else if (event.type === 'MessageReactionAdded' || event.type === 'MessageReactionRemoved') {
    try {
      const payload = JSON.parse(event.payloadJson) as Record<string, unknown>
      const messageId = String(payload.messageId ?? payload.MessageId ?? event.messageId)
      const reactions = (payload.reactions ?? payload.Reactions ?? {}) as Record<string, string[]>

      messagesStore.setMessageReactions(messageId, reactions)
    } catch (error) {
      console.error('[SignalR] Failed to parse MessageReaction payload:', error)
      Sentry.captureException(error, { extra: { action: 'parse_message_reaction_payload' } })
    }
  } else if (event.type === 'UserTyping') {
    try {
      const payload = JSON.parse(event.payloadJson) as Record<string, unknown>
      const channelId = String(payload.channelId ?? payload.ChannelId ?? event.chatId)
      const userId = String(payload.userId ?? payload.UserId ?? event.userId)
      const username = String(payload.username ?? payload.Username ?? '')
      const isTyping = Boolean(payload.isTyping ?? payload.IsTyping)

      if (userId && userId !== userStore.user?.id) {
        messagesStore.setUserTyping(channelId, userId, username, isTyping)
      }
    } catch (error) {
      console.error('[SignalR] Failed to parse UserTyping payload:', error)
      Sentry.captureException(error, { extra: { action: 'parse_user_typing_payload' } })
    }
  } else if (event.type === 'BotTyping') {
    try {
      const payload = JSON.parse(event.payloadJson) as Record<string, unknown>
      const channelId = dualCaseString(payload, 'channelId', 'ChannelId') || event.chatId
      const botId = dualCaseString(payload, 'botId', 'BotId') || event.userId
      const username = dualCaseString(payload, 'username', 'Username')
      const stateIndex = dualCaseNumber(payload, 'state', 'State')
      const isActive = dualCaseBoolean(payload, 'isActive', 'IsActive')
      const state = BOT_TYPING_STATES[stateIndex] ?? 'Thinking'

      if (botId) messagesStore.setBotTyping(channelId, botId, username, state, isActive)
    } catch (error) {
      console.error('[SignalR] Failed to parse BotTyping payload:', error)
      Sentry.captureException(error, { extra: { action: 'parse_bot_typing_payload' } })
    }
  }
}

function handleChannelEvent(event: KafkaMessageEvent) {
  const groupsStore = useGroupsStore()

  console.log('[SignalR] Received channel event:', event)

  if (event.type === 'UserConnected') {
    try {
      const payload = JSON.parse(event.payloadJson) as Record<string, unknown>
      const user = mapRawGroupUser(payload, event.userId)
      groupsStore.addVoiceChannelUser(event.groupId, event.chatId, user)
    } catch (error) {
      console.error('[SignalR] Failed to parse UserConnected payload:', error)
      Sentry.captureException(error, { extra: { action: 'parse_user_connected_payload' } })
    }
  } else if (event.type === 'UserDisconnected') {
    groupsStore.removeVoiceChannelUser(event.groupId, event.chatId, event.userId)
  } else if (event.type === 'ChannelUpdated') {
    // Published by the Channels service with ids only — see GROUP_STRUCTURE_EVENTS.
    reloadGroupStructure(event.groupId)
  }
}

function handleGroupEvent(event: KafkaMessageEvent) {
  const groupsStore = useGroupsStore()
  const userStore = useUserStore()
  const memberListStore = useChannelMemberListStore()

  console.log('[SignalR] Received group event:', event)

  if (GROUP_SNAPSHOT_EVENTS.has(event.type)) {
    try {
      const payload = JSON.parse(event.payloadJson) as Record<string, unknown>
      groupsStore.applyGroupSnapshot(payload, event.type)
    } catch (error) {
      console.error('[SignalR] Failed to parse group event payload:', error)
      Sentry.captureException(error, { extra: { action: 'parse_group_event_payload', eventType: event.type } })
    }
  } else if (GROUP_STRUCTURE_EVENTS.has(event.type)) {
    reloadGroupStructure(event.groupId)
  } else if (ROLE_DEFINITION_EVENTS.has(event.type)) {
    // A role's name, colour, permissions, position or «show separately» flag — role badges,
    // colours, permissions and the member list's sections all hang on these.
    memberListStore.invalidate(event.groupId)
    scheduleReload(`roles:${event.groupId}`, () => {
      void groupsStore.loadGroupRoles(event.groupId, `SignalR:${event.type}`, true)
    })
    // Permissions changed: which channels this user can see may have changed too.
    if (event.type !== 'RoleMoved') reloadGroupStructure(event.groupId)
  } else if (ROLE_ASSIGNMENT_EVENTS.has(event.type)) {
    // The member list's refetch brings the member back with their new role ids (and merges them
    // into the group's member cache). Our own roles decide our permissions and visible channels.
    memberListStore.invalidate(event.groupId)
    if (event.userId === userStore.user?.id) {
      scheduleReload(`self-roles:${event.groupId}`, () => {
        void groupsStore.loadGroupUsers(event.groupId, `SignalR:${event.type}`, true)
      })
      reloadGroupStructure(event.groupId)
    }
  } else if (event.type === 'UserJoined') {
    // The join event's payload doesn't carry enough to append the new member locally (unlike
    // `UserLeft`/`UserKicked` below, which just remove by id) — `force: true` bypasses the
    // now cache-first `loadGroupUsers` specifically for this one real server-pushed change.
    groupsStore.loadGroupUsers(event.groupId, 'SignalR:UserJoined', true)
    memberListStore.invalidate(event.groupId)
  } else if (event.type === 'UserLeft' || event.type === 'UserKicked' || event.type === 'UserBanned') {
    if (event.userId === userStore.user?.id) {
      groupsStore.handleSelfKicked(event.groupId)
    } else {
      groupsStore.removeGroupUser(event.groupId, event.userId)
      memberListStore.invalidate(event.groupId)
    }
  } else if (event.type === 'UserUnbanned') {
    groupsStore.removeGroupBan(event.groupId, event.userId)
  } else if (event.type === 'BoardChanged') {
    // Ids only (no card content): refetch just those cards with our own permissions, if that board is loaded.
    try {
      const payload = JSON.parse(event.payloadJson) as Record<string, unknown>
      const channelId = dualCaseString(payload, 'channelId', 'ChannelId') || event.chatId
      // `IssueIds` is the name the backend plan used — accepted too until the event is pinned down.
      const cardIds = [
        ...dualCaseStringArray(payload, 'cardIds', 'CardIds'),
        ...dualCaseStringArray(payload, 'issueIds', 'IssueIds'),
      ]
      if (channelId) void useBoardsStore().applyBoardChanged(channelId, cardIds)
    } catch (error) {
      console.error('[SignalR] Failed to parse BoardChanged payload:', error)
      Sentry.captureException(error, { extra: { action: 'parse_board_changed_payload' } })
    }
  } else if (event.type === 'UserStatusChanged') {
    try {
      const payload = JSON.parse(event.payloadJson) as Record<string, unknown>
      const userId = dualCaseString(payload, 'userId', 'UserId') || event.userId
      // NaN, not 0 (= ONLINE), when the field is missing: a payload without a status must not mark anyone online.
      const presenceStatus = dualCaseNumber(payload, 'presenceStatus', 'PresenceStatus', NaN)
      const status = dualCaseString(payload, 'status', 'Status')

      if (userId && !Number.isNaN(presenceStatus)) groupsStore.setUserPresenceStatus(userId, presenceStatus, status)
      // Online/offline moves the member between sections of the member lists.
      if (event.groupId) schedulePresenceRefresh(event.groupId)
    } catch (error) {
      console.error('[SignalR] Failed to parse UserStatusChanged payload:', error)
      Sentry.captureException(error, { extra: { action: 'parse_user_status_changed_payload' } })
    }
  } else if (event.type === 'UserProfileUpdated') {
    try {
      applyUserProfileUpdated(event)
    } catch (error) {
      console.error('[SignalR] Failed to parse UserProfileUpdated payload:', error)
      Sentry.captureException(error, { extra: { action: 'parse_user_profile_updated_payload' } })
    }
  }
}

watch(connection.isConnected, (connected) => {
  if (connected && isIdle.value) reportActivity()
})
watch(isIdle, reportActivity)

export function useGroupsSignalR() {
  return {
    isConnected: connection.isConnected,
    connect: connection.connect,
    reconnect: connection.reconnect,
    disconnect: connection.disconnect,
  }
}
